// The patient's own waitlist requests.
//
// Four calls, against `/patient/waitlist/*` — the door built for this portal
// rather than the staff routes under `/waitlist/*`. The patient is never sent:
// the server takes it from the session, so a request body that named somebody
// else would be ignored rather than trusted.
//
// Same axios shape as everything else in `crudRequests.js` — the bearer token
// out of `state.jwt` and the tenant header — so there is one request layer in
// this app and not two.

import axios from "axios";
import {url} from "../../crudRequests";

function authHeaders(state) {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + (state?.jwt || ""),
    // The LITERAL "patient", not the clinic's database name.
    // `middleware/cookieAuthMiddleware.js`: "Patient-side sessions are issued
    // under the literal tenant 'patient' rather than the clinic's database
    // name, so the portal can hold one cookie across organizations." Anything
    // else is compared against the token's own tenant and refused. `crud` in
    // `crudRequests.js` sends the same literal. The database itself is never
    // sent: the server reads it off the verified token.
    "X-Tenant": "patient",
  };
}

/**
 * Everything this patient is waiting on, in queue order.
 *
 * Not scoped by location: a request filed at the other site is still one they
 * are waiting on. `allowEdits` comes back per location, because a clinic can
 * stop accepting patient requests and the screen has to say so rather than
 * offer a form that will be refused. `askAt` is where they may ask for a new
 * one: each site that takes requests online, with the services a patient may
 * wait for there (the server's one rule, never re-derived here). A server
 * that does not say yet answers none, and the screen sends them to the clinic.
 */
export async function listMyWaitlistRequests(state) {
  const res = await axios({
    method: "GET",
    url: `${url}/patient/waitlist/entries`,
    headers: authHeaders(state),
  });
  return {
    entries: res.data?.entries || [],
    allowEdits: res.data?.allowEdits || {},
    askAt: Array.isArray(res.data?.askAt) ? res.data.askAt : [],
  };
}

/**
 * Ask to join the waitlist: the request as the drawer built it (requestForm.js
 * `bodyOf`). The server files it for the signed-in patient and answers it as
 * they read it, or refuses in words (a request already covered: 409).
 */
export async function askToJoinWaitlist(state, request) {
  const res = await axios({
    method: "POST",
    url: `${url}/patient/waitlist/entries`,
    headers: authHeaders(state),
    data: JSON.stringify(request),
  });
  return res.data;
}

/**
 * Change what they asked for.
 *
 * Editing is a close and a create, so the response says whether the place in
 * the queue survived: `keptQueuePosition` is true when the service is
 * unchanged. Read it rather than predicting it — the server owns that rule.
 */
export async function replaceMyWaitlistRequest(state, entryId, draft) {
  const res = await axios({
    method: "POST",
    url: `${url}/patient/waitlist/entries/${encodeURIComponent(entryId)}/replace`,
    headers: authHeaders(state),
    data: JSON.stringify(draft),
  });
  return res.data;
}

/** Come off the list. Recorded as the patient's own decision, always. */
export async function leaveMyWaitlistRequest(state, entryId) {
  const res = await axios({
    method: "DELETE",
    url: `${url}/patient/waitlist/entries/${encodeURIComponent(entryId)}`,
    headers: authHeaders(state),
  });
  return res.data;
}
